<template>
  <section class="batch-modify main-video">
    <header class="batch-head">
      <div class="crumb">
        <button class="plain" :disabled="locked" @click="goBack">‹ 返回</button>
        <span>
          商品工作台　›　批量修改　›　
          <strong>主图视频</strong>
        </span>
      </div>
      <h1>批量修改主图视频</h1>
      <nav>
        <button
          v-for="tab in tabs"
          :key="tab.value"
          :class="{ active: mode === tab.value }"
          :disabled="locked"
          @click="switchMode(tab.value)">
          {{ tab.label }}
        </button>
      </nav>
    </header>
    <main>
      <p v-if="optionsError" class="load-error">
        {{ optionsError }}
        <button class="plain" @click="loadOptions">重新加载</button>
      </p>
      <p v-else-if="!optionsLoading && !shopOptions.length" class="load-error">
        暂无可操作的授权抖店，请先完成店铺授权
      </p>
      <section class="panel">
        <div v-if="mode === 'products'" class="picked">
          <button :disabled="locked || !canQuery" @click="openSelector">选择商品</button>
          <span>
            已选择
            <b>{{ selected.length }}</b>
            个商品
          </span>
        </div>
        <template v-else-if="mode === 'product_ids'">
          <label>抖店商品ID</label>
          <el-input
            v-model="idsText"
            :disabled="locked"
            type="textarea"
            :rows="3"
            placeholder="多个ID用逗号、空格或换行分隔，最多2000个" />
          <label class="draft-control">
            是否草稿
            <el-select v-model="idDraft" :disabled="locked">
              <el-option label="全部" value="" />
              <el-option label="是" :value="1" />
              <el-option label="否" :value="0" />
            </el-select>
          </label>
        </template>
        <div v-else class="filters">
          <label v-for="field in shownFields" :key="field.key" class="field">
            <span>{{ field.label }}</span>
            <doudian-category-cascader
              v-if="field.key === 'category_id'"
              v-model="filters.category_id"
              :categories="categories"
              :disabled="locked" />
            <el-date-picker
              v-else-if="field.date"
              v-model="filters[field.key]"
              :disabled="locked"
              type="datetimerange"
              value-format="yyyy-MM-dd HH:mm:ss"
              start-placeholder="开始时间"
              end-placeholder="结束时间" />
            <div v-else-if="field.matchKey" class="combined-control">
              <el-select v-model="filters[field.matchKey]" :disabled="locked">
                <el-option
                  v-for="option in field.matchOptions"
                  :key="option.value"
                  :value="option.value"
                  :label="option.label" />
              </el-select>
              <div v-if="field.range" class="range">
                <input v-model="filters[field.key + '_min']" :disabled="locked" placeholder="最小值" />
                <span>~</span>
                <input v-model="filters[field.key + '_max']" :disabled="locked" placeholder="最大值" />
              </div>
              <input
                v-else
                v-model="filters[field.key]"
                :disabled="locked"
                :placeholder="field.placeholder || '请输入'" />
            </div>
            <div v-else-if="field.range" class="range">
              <input v-model="filters[field.key + '_min']" :disabled="locked" placeholder="最小值" />
              <span>~</span>
              <input v-model="filters[field.key + '_max']" :disabled="locked" placeholder="最大值" />
            </div>
            <el-select v-else-if="field.options" v-model="filters[field.key]" :disabled="locked" clearable>
              <el-option
                v-for="option in field.options"
                :key="String(option.value)"
                :value="option.value"
                :label="option.label" />
            </el-select>
            <input
              v-else
              v-model="filters[field.key]"
              :disabled="locked"
              :placeholder="field.placeholder || '请输入条件'" />
          </label>
        </div>
        <footer class="filter-actions">
          <doudian-batch-shop-scope
            v-model="shops"
            :shops="shopOptions"
            :disabled="locked || mode === 'products'" />
          <div class="filter-buttons">
            <button :disabled="locked || previewLoading || !canQuery" @click="loadPreview(1, true)">
              查询商品及原视频
            </button>
            <button class="plain" :disabled="locked" @click="reset">重置</button>
          </div>
        </footer>
        <p class="hint">
          先按本地数据筛选，执行时读取抖店最新详情复核。未选择店铺时使用当前账号有权限的全部抖店。
        </p>
      </section>
      <section class="panel methods">
        <h3>
          <i></i>
          修改内容
        </h3>
        <div class="rule-row">
          <el-radio v-model="ruleMode" label="delete" :disabled="locked">方式1：删除主图视频</el-radio>
          <span class="hint">删除商品上的主图视频</span>
        </div>
        <div class="rule-row video-rule">
          <el-radio v-model="ruleMode" label="set" :disabled="locked">方式2：选择视频</el-radio>
          <div class="video-controls">
            <el-radio-group v-model="sourceMode" :disabled="locked || ruleMode !== 'set'">
              <el-radio label="manual">手动选择视频</el-radio>
              <el-radio label="product">使用商品ID选择视频</el-radio>
            </el-radio-group>
            <template v-if="sourceMode === 'manual'">
              <button :disabled="locked || ruleMode !== 'set'" @click="selectorVideoVisible = true">
                {{ manualSource ? '重新选择视频' : '选择视频' }}
              </button>
            </template>
            <div v-else class="source-product">
              <el-select
                v-model="sourceShopId"
                :disabled="locked || sourceLoading || ruleMode !== 'set'"
                placeholder="来源店铺">
                <el-option
                  v-for="shop in shopOptions"
                  :key="shop.mer_shop_id"
                  :value="Number(shop.mer_shop_id)"
                  :label="shop.shop_name" />
              </el-select>
              <el-input
                v-model="sourceProductId"
                :disabled="locked || sourceLoading || ruleMode !== 'set'"
                placeholder="来源商品ID" />
              <button :disabled="locked || sourceLoading || ruleMode !== 'set'" @click="readProductSource">
                {{ sourceLoading ? '读取中…' : '读取视频' }}
              </button>
              <small>只读取来源商品当前视频，用于同一家店铺的目标商品。</small>
            </div>
            <doudian-video-preview
              v-if="currentSource"
              :available="true"
              :request-data="{ source_id: currentSource.source_id }"
              :cover-url="currentSource.preview && currentSource.preview.cover_url"
              :label="currentSource.name" />
            <el-checkbox v-model="skipExisting" :disabled="locked || ruleMode !== 'set'">
              已有主图视频时跳过
            </el-checkbox>
            <p v-if="sourceError" class="load-error">{{ sourceError }}</p>
          </div>
        </div>
      </section>
      <section v-if="preview" class="panel preview-panel">
        <h3>
          <i></i>
          {{ candidatesOnly ? '商品及原视频' : '修改内容预览' }}
        </h3>
        <p>
          范围共 {{ preview.total }} 个，已排除 {{ preview.excluded_total }} 个，选中
          {{ preview.selected_total }} 个。
        </p>
        <p class="hint">本地预览仅供参考，执行时以平台最新详情和审核结果为准。</p>
        <p v-if="preview.missing_product_ids && preview.missing_product_ids.length" class="load-error">
          本地未匹配ID：{{ preview.missing_product_ids.join('、') }}
        </p>
        <p v-if="!candidatesOnly && !previewCurrent" class="load-error">设置已变化，请重新预览后开始。</p>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>商品信息</th>
                <th>原视频</th>
                <th>目标视频</th>
                <th>状态及原因</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in preview.list" :key="row.id" :class="{ excluded: excluded.includes(row.id) }">
                <td>
                  <div class="preview-product">
                    <img v-if="row.main_image" :src="row.main_image" alt="商品主图" />
                    <div>
                      {{ row.product_title }}
                      <small>
                        {{ row.shop_name }}
                        <br />
                        {{ row.platform_product_id }}
                      </small>
                    </div>
                  </div>
                </td>
                <td>
                  <doudian-video-preview
                    :available="!!(row.old_video && row.old_video.known && row.old_video.product_video_id)"
                    :request-data="{ shop_id: row.shop_id, platform_product_id: row.platform_product_id }"
                    :cover-url="row.old_video && row.old_video.cover_url"
                    :label="
                      row.old_video && row.old_video.known
                        ? row.old_video.product_video_id
                          ? '播放原视频'
                          : '无视频'
                        : '视频信息待核验'
                    " />
                </td>
                <td>
                  <template v-if="row.target_video">
                    <doudian-video-preview
                      v-if="row.target_video.action === 'set'"
                      :available="!!row.target_video.source_id"
                      :request-data="{ source_id: row.target_video.source_id }"
                      :cover-url="row.target_video.cover_url"
                      :label="row.target_video.name || '目标视频'" />
                    <span v-else>删除商品主图视频</span>
                  </template>
                  <span v-else>尚未预览</span>
                </td>
                <td>
                  <span>{{ resultLabels[row.preview_result] || '待核验' }}</span>
                  <p>{{ row.error_msg || (candidatesOnly ? '查询结果，尚未预览修改' : '执行时再次核验') }}</p>
                </td>
                <td>
                  <el-checkbox
                    :value="excluded.includes(row.id)"
                    :disabled="locked || previewLoading"
                    @change="exclude(row.id, $event)">
                    排除修改
                  </el-checkbox>
                </td>
              </tr>
              <tr v-if="!preview.list.length"><td colspan="5" class="empty">暂无符合条件的商品</td></tr>
            </tbody>
          </table>
        </div>
        <el-pagination
          layout="prev, pager, next"
          :disabled="locked || previewLoading"
          :current-page="preview.page"
          :page-size="20"
          :total="preview.total"
          @current-change="loadPreview($event, candidatesOnly)" />
      </section>
      <p v-if="previewError" class="load-error">{{ previewError }}</p>
    </main>
    <footer class="actions">
      <button
        :disabled="locked || !canQuery || sourceLoading || previewLoading"
        @click="loadPreview(1, false)">
        {{ previewLoading ? '预览中…' : '修改内容预览' }}
      </button>
      <button v-if="createUncertain" class="start" :disabled="submitting" @click="submitFrozen">
        确认创建结果
      </button>
      <button v-else class="start" :disabled="!canCreate" @click="createBatch">
        {{ submitting ? '创建中…' : '开始批量修改' }}
      </button>
      <label>
        <input v-model="scheduled" type="checkbox" :disabled="locked" />
        定时执行
      </label>
      <el-date-picker
        v-if="scheduled"
        v-model="scheduledAt"
        :disabled="locked"
        type="datetime"
        value-format="yyyy-MM-dd HH:mm:ss"
        placeholder="选择北京时间" />
      <small v-if="createUncertain" class="load-error">
        创建结果尚未确认，请使用原请求确认，勿重复新建。
      </small>
    </footer>
    <doudian-video-selector-dialog
      :visible.sync="selectorVideoVisible"
      :shops="shopOptions"
      @selected="acceptSource" />
    <product-selector-dialog
      :page-sizes="[20, 50, 100, 500, 1000]"
      @page-size-change="changeSelectorPageSize"
      :visible="selectorVisible"
      :active-tab="selectorTab"
      :products="selectorProducts"
      :selected-ids="draftSelected.map(productId)"
      :loading="selectorLoading"
      :page="selectorPage"
      :page-size="selectorPageSize"
      :total="selectorTab === 'selected' ? draftSelected.length : selectorTotal"
      :total-pages="selectorPages"
      :jump-page="Number(selectorJump)"
      :page-fully-selected="selectorProducts.length > 0 && selectorProducts.every(isSelected)"
      :product-id="productId"
      :product-key="productId"
      :product-title="productTitle"
      :product-image="productImage"
      :product-price="productPrice"
      @tab-change="changeSelectorTab"
      @close="selectorVisible = false"
      @toggle-product="toggleProduct"
      @toggle-page="togglePage"
      @page-change="changeSelectorPage"
      @jump-input="selectorJump = $event"
      @jump="changeSelectorPage(Number(selectorJump))"
      @confirm="confirmSelected">
      <template #filters>
        <doudian-product-selector-filters
          :value="selectorFilters"
          :shop-id="selectorShopId"
          :shops="shopOptions"
          :categories="categories"
          @shop-change="changeSelectorShop"
          @query="loadSelector(1)" />
      </template>
    </product-selector-dialog>
  </section>
</template>

<script>
import DoudianBatchShopScope from '@/components/DoudianBatchShopScope'
import DoudianCategoryCascader from '@/components/DoudianCategoryCascader'
import ProductSelectorDialog from '@/components/ProductSelectorDialog/ProductSelectorDialog'
import DoudianVideoSelectorDialog from '@/components/DoudianVideoSelectorDialog.vue'
import DoudianVideoPreview from '@/components/DoudianVideoPreview.vue'
import DoudianProductSelectorFilters from './DoudianProductSelectorFilters.vue'
import { fields, pairedFields, defaults, buildBatchProductFilter } from './batchModifyFilters'
import {
  getAuthSuccessShopList,
  getDoudianCategories,
  getProductSelectorProducts,
  registerDoudianMainVideoSource,
  getDoudianMainVideoCandidates,
  previewDoudianMainVideoBatch,
  createDoudianMainVideoBatch
} from '@/api/main/doudianBatchEdit'

const allowedFields = [
  'category_id',
  'status',
  'title_mode',
  'product_title',
  'platform_product_id',
  'stock',
  'price_mode',
  'price',
  'created_at',
  'brand_id',
  'presell',
  'freight_id',
  'is_distribute',
  'product_no_mode',
  'product_no',
  'sales',
  'is_draft',
  'sku_code_mode',
  'sku_code',
  'sku_name_mode',
  'sku_name',
  'fabric_material',
  'has_video'
]
const uuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 3) | 8).toString(16)
  })
export default {
  name: 'BatchModifyMainVideo',
  components: {
    DoudianBatchShopScope,
    DoudianCategoryCascader,
    ProductSelectorDialog,
    DoudianProductSelectorFilters,
    DoudianVideoSelectorDialog,
    DoudianVideoPreview
  },
  data: () => ({
    tabs: [
      { value: 'range', label: '按范围修改' },
      { value: 'products', label: '按商品修改' },
      { value: 'product_ids', label: '按商品ID修改' }
    ],
    mode: 'range',
    filters: defaults(),
    shops: [],
    shopOptions: [],
    categories: [],
    selected: [],
    idsText: '',
    idDraft: '',
    excluded: [],
    optionsLoading: false,
    optionsError: '',
    ruleMode: 'delete',
    sourceMode: 'manual',
    skipExisting: false,
    manualSource: null,
    productSource: null,
    sourceShopId: null,
    sourceProductId: '',
    sourceLoading: false,
    sourceError: '',
    sourceSequence: 0,
    sourceInput: null,
    selectorVideoVisible: false,
    preview: null,
    previewLoading: false,
    previewError: '',
    previewSignature: '',
    previewRequest: 0,
    candidatesOnly: true,
    scheduled: false,
    scheduledAt: '',
    requestId: uuid(),
    frozenRequest: null,
    submitting: false,
    confirming: false,
    createUncertain: false,
    selectorVisible: false,
    selectorTab: 'all',
    selectorRows: [],
    selectorPageSize: 20,
    selectorTotal: 0,
    selectorPage: 1,
    selectorJump: 1,
    selectorShopId: '',
    selectorLoading: false,
    selectorFilters: {
      category_id: { level1: [], level2: [], level3: [] },
      platform_product_id: '',
      product_title: '',
      created_at: [],
      tab_status: ''
    },
    draftSelected: [],
    selectorRequest: 0,
    resultLabels: { ready: '预计命中', skipped: '预计跳过', unknown: '待核验' }
  }),
  computed: {
    locked() {
      return this.submitting || this.confirming || this.createUncertain
    },
    canQuery() {
      return !this.optionsLoading && !this.optionsError && this.shopOptions.length > 0
    },
    currentSource() {
      return this.sourceMode === 'manual' ? this.manualSource : this.productSource
    },
    previewCurrent() {
      try {
        return !this.candidatesOnly && this.previewSignature === JSON.stringify(this.previewIntent())
      } catch (_) {
        return false
      }
    },
    canCreate() {
      return (
        this.canQuery &&
        this.previewCurrent &&
        this.preview &&
        this.preview.total <= 10000 &&
        this.preview.selected_total > 0 &&
        !this.previewLoading &&
        !this.previewError &&
        !this.locked &&
        !this.sourceLoading
      )
    },
    shownFields() {
      return fields
        .filter((f) => allowedFields.includes(f.key) && !Object.values(pairedFields).includes(f.key))
        .map((f) =>
          pairedFields[f.key]
            ? {
                ...f,
                matchKey: pairedFields[f.key],
                matchOptions: fields.find((x) => x.key === pairedFields[f.key]).options
              }
            : f
        )
    },
    selectorPages() {
      return Math.max(
        1,
        Math.ceil((this.selectorTab === 'selected' ? this.draftSelected.length : this.selectorTotal) / this.selectorPageSize)
      )
    },
    selectorProducts() {
      return this.selectorTab === 'selected'
        ? this.draftSelected.slice((this.selectorPage - 1) * this.selectorPageSize, this.selectorPage * this.selectorPageSize)
        : this.selectorRows
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    shops: {
      deep: true,
      handler() {
        this.invalidate()
      }
    },
    idsText() {
      this.invalidate()
    },
    idDraft() {
      this.invalidate()
    },
    ruleMode() {
      this.invalidate()
    },
    sourceMode() {
      this.invalidate()
    },
    skipExisting() {
      this.invalidate()
    },
    sourceShopId() {
      this.clearProductSource()
    },
    sourceProductId() {
      this.clearProductSource()
    },
    scheduled() {
      this.requestId = uuid()
      if (!this.scheduled) this.scheduledAt = ''
    },
    scheduledAt() {
      if (!this.createUncertain) this.requestId = uuid()
    }
  },
  created() {
    this.loadOptions()
  },
  beforeDestroy() {
    this.previewRequest++
    this.selectorRequest++
    this.sourceSequence++
  },
  methods: {
    changeSelectorPageSize(size) {
      this.selectorPageSize = Number(size)
      this.selectorPage = 1
      this.loadSelector(1)
    },
    dataOf(response) {
      if (!response || Number(response.code) !== 200) {
        const e = new Error(response?.message || '请求失败')
        e.businessCode = Number(response?.code)
        throw e
      }
      return response.data
    },
    message(error) {
      return error?.response?.data?.message || error?.message || '请求失败，请稍后重试'
    },
    async loadOptions() {
      this.optionsLoading = true
      this.optionsError = ''
      try {
        const [shops, categories] = await Promise.all([
          getAuthSuccessShopList(),
          getDoudianCategories({ shop_type: 1 })
        ])
        const shopData = this.dataOf(shops),
          cateData = this.dataOf(categories)
        this.shopOptions = Array.isArray(shopData) ? shopData : shopData.list || shopData.data || []
        const map = (rows) =>
          rows.map((x) => ({
            ...x,
            category_id: x.id || x.category_id,
            category_name: x.name || x.category_name,
            children: map(x.children || [])
          }))
        this.categories = map(Array.isArray(cateData) ? cateData : cateData.list || cateData.data || [])
      } catch (e) {
        this.optionsError = this.message(e)
      } finally {
        this.optionsLoading = false
      }
    },
    parseIds(text) {
      const ids = [
        ...new Set(
          String(text)
            .split(/[,，\s]+/)
            .map((x) => x.trim())
            .filter(Boolean)
        )
      ]
      const invalid = ids.filter((x) => !/^[1-9][0-9]{0,199}$/.test(x))
      if (invalid.length) throw new Error(`商品 ID 无效：${invalid[0]}，共${invalid.length}项`)
      return ids
    },
    productFilter() {
      if (this.mode === 'products')
        return {
          shop_ids: [...this.shops],
          platform_product_id: [...new Set(this.selected.map((x) => String(x.platform_product_id)))],
          excluded_product_ids: [...this.excluded]
        }
      if (this.mode === 'product_ids')
        return {
          shop_ids: [...this.shops],
          platform_product_id: this.parseIds(this.idsText),
          excluded_product_ids: [...this.excluded],
          ...(this.idDraft !== '' ? { is_draft: this.idDraft } : {})
        }
      const active = {}
      fields
        .filter((f) => allowedFields.includes(f.key))
        .forEach((f) => {
          if (f.range) {
            active[f.key + '_min'] = this.filters[f.key + '_min']
            active[f.key + '_max'] = this.filters[f.key + '_max']
          } else active[f.key] = this.filters[f.key]
        })
      if (!Object.values(active.category_id).some((v) => v.length)) delete active.category_id
      if (!Array.isArray(active.created_at) || active.created_at.length !== 2) delete active.created_at
      Object.entries(pairedFields).forEach(([key, modeKey]) => {
        if (
          !active[key] &&
          !(key === 'price' && (active.price_min !== '' || active.price_max !== '')) &&
          !(key === 'sku_code' && active[modeKey] === 'empty')
        )
          delete active[modeKey]
      })
      if (active.sku_code_mode === 'empty') delete active.sku_code
      return buildBatchProductFilter(active, this.shops, this.excluded, 'range')
    },
    productId(row) {
      return Number(row.distribute_product_id || row.id)
    },
    productTitle(row) {
      return row.product_title || row.title || ''
    },
    productImage(row) {
      return row.main_image || row.img || ''
    },
    productPrice(row) {
      return row.product_price == null ? '价格待定' : '¥' + Number(row.product_price).toFixed(2)
    },
    isSelected(row) {
      return this.draftSelected.some((x) => this.productId(x) === this.productId(row))
    },
    openSelector() {
      this.draftSelected = JSON.parse(JSON.stringify(this.selected))
      this.selectorShopId = this.selected[0]?.shop_id || ''
      this.selectorTab = 'all'
      this.selectorVisible = true
      this.loadSelector(1)
    },
    toggleProduct(row) {
      if (
        !this.isSelected(row) &&
        this.draftSelected.length &&
        Number(this.draftSelected[0].shop_id) !== Number(row.shop_id)
      )
        return this.$message.warning('只能选择同一家店铺的商品')
      if (!this.isSelected(row) && this.draftSelected.length >= 10000)
        return this.$message.warning('最多选择10000个商品')
      this.draftSelected = this.isSelected(row)
        ? this.draftSelected.filter((x) => this.productId(x) !== this.productId(row))
        : [...this.draftSelected, row]
    },
    togglePage() {
      const rows = [...this.selectorProducts]
      const all = rows.every(this.isSelected)
      rows.forEach((row) => {
        if (this.isSelected(row) === all) this.toggleProduct(row)
      })
    },
    changeSelectorTab(tab) {
      this.selectorTab = tab
      this.changeSelectorPage(1)
    },
    changeSelectorPage(page) {
      const p = Math.max(1, Math.min(this.selectorPages, Number(page) || 1))
      if (this.selectorTab === 'all') this.loadSelector(p)
      else {
        this.selectorPage = p
        this.selectorJump = p
      }
    },
    changeSelectorShop(id) {
      this.selectorShopId = id
      this.loadSelector(1)
    },
    confirmSelected() {
      if (this.draftSelected.some((x) => Number(x.shop_id) !== Number(this.draftSelected[0].shop_id)))
        return this.$message.warning('只能选择同一家店铺的商品')
      this.selected = [...this.draftSelected]
      this.shops = this.selected.length ? [Number(this.selected[0].shop_id)] : []
      this.excluded = []
      this.selectorVisible = false
      this.invalidate()
    },
    async loadSelector(page) {
      const request = ++this.selectorRequest
      this.selectorLoading = true
      const { created_at, ...filters } = this.selectorFilters
      try {
        const data = this.dataOf(
          await getProductSelectorProducts({
            page,
            page_size: this.selectorPageSize,
            ...filters,
            ...(created_at?.length === 2 ? { created_at } : {}),
            ...(this.selectorShopId ? { shop_ids: [this.selectorShopId] } : {})
          })
        )
        if (request !== this.selectorRequest) return
        this.selectorRows = data.list || []
        this.selectorTotal = Number(data.pagination?.total || data.total || 0)
        this.selectorPage = page
        this.selectorJump = page
      } catch (e) {
        if (request === this.selectorRequest) this.$message.error(this.message(e))
      } finally {
        if (request === this.selectorRequest) this.selectorLoading = false
      }
    },

    invalidate() {
      this.previewRequest++
      this.previewLoading = false
      this.previewSignature = ''
      if (!this.createUncertain) this.requestId = uuid()
    },
    switchMode(mode) {
      this.mode = mode
      this.selected = []
      this.excluded = []
      this.preview = null
      this.invalidate()
    },
    reset() {
      this.filters = defaults()
      this.shops = []
      this.selected = []
      this.idsText = ''
      this.idDraft = ''
      this.excluded = []
      this.preview = null
      this.previewError = ''
      this.invalidate()
    },
    clearProductSource() {
      this.sourceSequence++
      this.sourceLoading = false
      this.productSource = null
      this.sourceInput = null
      this.sourceError = ''
      this.invalidate()
    },
    acceptSource(source) {
      this.manualSource = source
      this.invalidate()
    },
    async readProductSource() {
      if (!this.sourceShopId || !/^[1-9][0-9]{0,199}$/.test(this.sourceProductId.trim()))
        return this.$message.warning('请选择来源店铺并输入有效商品ID')
      const seq = ++this.sourceSequence
      this.sourceLoading = true
      this.sourceError = ''
      if (!this.sourceInput)
        this.sourceInput = {
          source_kind: 'product',
          request_key: uuid(),
          source_shop_id: this.sourceShopId,
          source_product_id: this.sourceProductId.trim()
        }
      try {
        const res = await registerDoudianMainVideoSource(this.sourceInput)
        if (seq !== this.sourceSequence || this._isDestroyed) return
        this.productSource = res.data
        this.sourceInput = null
        this.invalidate()
      } catch (error) {
        if (seq === this.sourceSequence) this.sourceError = this.message(error)
      } finally {
        if (seq === this.sourceSequence) this.sourceLoading = false
      }
    },
    selectionIntent() {
      const filter = this.productFilter()
      if (this.mode !== 'range' && !filter.platform_product_id.length) throw new Error('请先选择或输入商品')
      if (this.mode === 'product_ids' && filter.platform_product_id.length > 2000)
        throw new Error('商品ID最多2000个')
      return { selection_mode: this.mode, product_filter: filter }
    },
    previewIntent() {
      if (this.ruleMode === 'set' && !this.currentSource?.source_id) throw new Error('请先选择或读取目标视频')
      return {
        ...this.selectionIntent(),
        rule:
          this.ruleMode === 'delete'
            ? { mode: 'delete' }
            : { mode: 'set', source_id: this.currentSource.source_id, skip_existing: this.skipExisting }
      }
    },
    async loadPreview(page = 1, candidates = false) {
      let intent
      try {
        intent = candidates ? this.selectionIntent() : this.previewIntent()
      } catch (error) {
        return this.$message.warning(error.message)
      }
      const seq = ++this.previewRequest
      const signature = JSON.stringify(intent)
      this.previewLoading = true
      this.previewError = ''
      try {
        const res = await (candidates ? getDoudianMainVideoCandidates : previewDoudianMainVideoBatch)({
          ...intent,
          page,
          limit: 20
        })
        if (seq !== this.previewRequest || this._isDestroyed) return
        this.preview = res.data
        this.candidatesOnly = candidates
        this.previewSignature = candidates ? '' : signature
      } catch (error) {
        if (seq === this.previewRequest) {
          this.previewError = this.message(error)
          this.previewSignature = ''
        }
      } finally {
        if (seq === this.previewRequest) this.previewLoading = false
      }
    },
    exclude(id, value) {
      this.excluded = value ? [...new Set([...this.excluded, id])] : this.excluded.filter((x) => x !== id)
      this.invalidate()
      this.loadPreview(this.preview.page, this.candidatesOnly)
    },
    async createBatch() {
      if (!this.canCreate) return
      if (this.scheduled) {
        const time = new Date(String(this.scheduledAt).replace(' ', 'T') + '+08:00').getTime()
        if (!Number.isFinite(time) || time <= Date.now()) return this.$message.warning('请选择未来的北京时间')
      }
      const request = {
        ...this.previewIntent(),
        request_id: this.requestId,
        ...(this.scheduled ? { scheduled_at: this.scheduledAt } : {})
      }
      this.confirming = true
      try {
        await this.$confirm(
          `确认对选中的${this.preview.selected_total}个商品${
            this.ruleMode === 'delete'
              ? '删除主图视频'
              : this.skipExisting
              ? '设置主图视频，已有视频时跳过'
              : '设置主图视频并覆盖已有视频'
          }？执行时将重新核验商品状态。`,
          '确认批量修改',
          { confirmButtonText: '确认创建', cancelButtonText: '返回检查' }
        )
        this.frozenRequest = JSON.parse(JSON.stringify(request))
      } catch (_) {
        return
      } finally {
        this.confirming = false
      }
      await this.submitFrozen()
    },
    async submitFrozen() {
      if (!this.frozenRequest || this.submitting) return
      this.submitting = true
      try {
        const res = await createDoudianMainVideoBatch(this.frozenRequest)
        this.createUncertain = false
        this.$message.success(`已创建主图视频任务，实际入批${res.data.total}个商品`)
        this.$emit('created', res.data)
      } catch (error) {
        const code = Number(error.code || error.response?.data?.code)
        this.createUncertain = ![401, 403, 422].includes(code)
        this.$message.error(this.message(error))
      } finally {
        this.submitting = false
      }
    },
    async goBack() {
      if (this.currentSource || this.selected.length || this.idsText || this.preview) {
        try {
          await this.$confirm('离开后将丢失当前设置，确定返回吗？', '返回批量修改')
        } catch (_) {
          return
        }
      }
      this.$emit('back')
    }
  }
}
</script>

<style scoped src="./batch-modify.css"></style>
<style scoped>
.main-video {
  min-width: 0;
}
.main-video > main {
  padding-bottom: 130px;
}
.rule-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 15px;
  margin: 16px 0;
}
.video-controls {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  min-width: 220px;
  align-items: flex-start;
}
.source-product {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.source-product .el-input,
.source-product .el-select {
  width: 200px;
}
.source-product small {
  flex-basis: 100%;
  color: #888;
}
.draft-control {
  display: flex;
  gap: 10px;
  align-items: center;
  margin: 10px 0;
}
.load-error {
  color: #c43b34;
  overflow-wrap: anywhere;
}
.table-wrap {
  overflow-x: auto;
}
.main-video table {
  min-width: 850px;
  width: 100%;
  border-collapse: collapse;
}
.main-video th,
.main-video td {
  padding: 12px;
  text-align: center;
  vertical-align: middle;
  border-bottom: 1px solid #e5e8ee;
  font-size: 12px;
}
.main-video th:first-child,
.main-video td:first-child {
  text-align: left;
  width: 32%;
}
.preview-product {
  display: flex;
  align-items: center;
  gap: 10px;
  overflow-wrap: anywhere;
}
.preview-product img {
  width: 56px;
  height: 56px;
  object-fit: cover;
}
.preview-product small {
  display: block;
  margin-top: 5px;
  color: #999;
}
.excluded {
  opacity: 0.6;
}
.actions {
  flex-wrap: wrap;
  gap: 10px;
}
@media (max-width: 680px) {
  .source-product .el-input,
  .source-product .el-select {
    width: 100%;
  }
  .video-controls {
    min-width: 0;
    flex-basis: 100%;
  }
}
</style>
